import { Grid } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import Overview from "./Overview";
import OverviewBar from "./OverviewBar";
import StatusMachine from "./StatusMachine";
import db from "../../../firebase-config";

import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

export default function Production() {
  const getCurrentFactoryDate = () => {
    const now = dayjs();
    return now.hour() < 6 ? now.subtract(1, "day") : now;
  };

  const [selectedDate, setSelectedDate] = useState(getCurrentFactoryDate());
  const [oeeDashboard, setOeeDashboard] = useState([]);
  const [oeeProcess, setOeeProcess] = useState([]);

  const currentFactoryDate = useMemo(() => getCurrentFactoryDate(), []);

  const selected = useMemo(
    () => selectedDate.format("YYYY-MM-DD"),
    [selectedDate]
  );

  const nextSelected = useMemo(
    () => selectedDate.add(1, "day").format("YYYY-MM-DD"),
    [selectedDate]
  );

  const isCurrentFactoryDate = useMemo(
    () => selectedDate.isSame(currentFactoryDate, "day"),
    [selectedDate, currentFactoryDate]
  );

  const startOfPeriod = useMemo(
    () =>
      selectedDate
        .hour(6)
        .minute(0)
        .second(0)
        .millisecond(0),
    [selectedDate]
  );

  const endOfPeriod = useMemo(
    () => startOfPeriod.add(1, "day"),
    [startOfPeriod]
  );

  useEffect(() => {
    setOeeDashboard([]);
    let unsubscribe = null;

    const factoryDate = selectedDate.format("YYYY-MM-DD");

    const baseQuery = db
      .collection("prod_mes_ignition_oee")
      .where("LINE", "==", "LINE7");

    const query = baseQuery.where("FACTORY_DATE", "==", factoryDate);

    const parseLatestTime = (data) => {
      return dayjs(
        data.UPDATE_AT ||
          data.UPDATED_AT ||
          data.FINISH_DATETIME ||
          data.START_TIME ||
          data.PLAN_START ||
          data.id,
        ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"],
        true
      );
    };

    const setLatestDocFromSnapshot = (querySnapshot) => {
      const oeeDocs = [];

      querySnapshot.forEach((doc) => {
        oeeDocs.push({ ...doc.data(), id: doc.id });
      });

      const sortedDocs = oeeDocs.sort((a, b) => {
        const timeA = parseLatestTime(a);
        const timeB = parseLatestTime(b);

        if (!timeA.isValid()) return 1;
        if (!timeB.isValid()) return -1;

        return timeA.diff(timeB);
      });

      const lastOee = sortedDocs[sortedDocs.length - 1];

      setOeeDashboard(lastOee || []);
    };

    const fallbackToLatestFactoryDate = async () => {
      try {
        const latestSnapshot = await baseQuery
          .orderBy("FACTORY_DATE", "desc")
          .limit(1)
          .get();

        if (latestSnapshot.empty) {
          setOeeDashboard([]);
          return;
        }

        const latestDocRef = latestSnapshot.docs[0];
        const latestDoc = latestDocRef.data();
        const latestFactoryDate = latestDoc.FACTORY_DATE;

        if (latestFactoryDate && latestFactoryDate !== factoryDate) {
          setSelectedDate(dayjs(latestFactoryDate, "YYYY-MM-DD"));
        } else {
          setOeeDashboard({
            ...latestDoc,
            id: latestDocRef.id,
          });
        }
      } catch (error) {
        console.error("Fallback latest FACTORY_DATE error:", error);
        setOeeDashboard([]);
      }
    };

    const handleSnapshot = async (querySnapshot) => {
      if (querySnapshot.empty && isCurrentFactoryDate) {
        await fallbackToLatestFactoryDate();
        return;
      }

      setLatestDocFromSnapshot(querySnapshot);
    };

    if (isCurrentFactoryDate) {
      unsubscribe = query.onSnapshot(handleSnapshot);
    } else {
      query.get().then(handleSnapshot);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedDate, isCurrentFactoryDate]);

  useEffect(() => {
    setOeeProcess([]);
    let unsubscribe = null;

    const query = db
      .collection("prod_mes_ignition_oee_process")
      .where("LINE", "==", "LINE7")
      .where("DATE", "in", [selected, nextSelected]);

    const handleSnapshot = (querySnapshot) => {
      const result = [];

      querySnapshot.forEach((doc) => {
        const data = { ...doc.data(), id: doc.id };

        const startTime = dayjs(data.START, "YYYY-MM-DD HH:mm:ss", true);
        const endTime = dayjs(data.END, "YYYY-MM-DD HH:mm:ss", true);

        if (!startTime.isValid() || !endTime.isValid()) return;

        const isInProductionDay =
          startTime.isSameOrAfter(startOfPeriod) &&
          startTime.isBefore(endOfPeriod);

        if (!isInProductionDay) return;

        result.push(data);
      });

      result.sort((a, b) =>
        dayjs(a.START, "YYYY-MM-DD HH:mm:ss").diff(
          dayjs(b.START, "YYYY-MM-DD HH:mm:ss")
        )
      );

      setOeeProcess(result);
    };

    if (isCurrentFactoryDate) {
      unsubscribe = query.onSnapshot(handleSnapshot);
    } else {
      query.get().then(handleSnapshot);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selected, nextSelected, startOfPeriod, endOfPeriod, isCurrentFactoryDate]);

  const getValue = (val) => {
    if (val === undefined || val === null || val === "NA" || val === "-") {
      return null;
    }

    const numberValue = Number(val);
    return Number.isNaN(numberValue) ? null : numberValue;
  };

  const electricity = getValue(oeeDashboard?.V2_ELECTRICITY_CONSUMPTION);
  const electricityDisplay =
    electricity === null ? "-" : Math.round(electricity).toLocaleString();

  const fuel = getValue(oeeDashboard?.FUEL_CONSUMPTION);
  const fuelDisplay = fuel === null ? "-" : Math.round(fuel).toLocaleString();

  const water = getValue(oeeDashboard?.V2_WATER_CONSUMPTION);
  const waterDisplay =
    water === null ? "-" : Math.round(water).toLocaleString();

  const totalEnergy = (electricity || 0) + (fuel || 0) + (water || 0);
  const totalEnergyDisplay =
    electricity === null && fuel === null && water === null
      ? "-"
      : Math.round(totalEnergy).toLocaleString();

  const productCost = getValue(oeeDashboard?.V2_ENERGY_PROD_COST_BAHT_PER_KG);
  const productCostDisplay =
    productCost === null
      ? "-"
      : Number(productCost).toLocaleString(undefined, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });

  return (
    <Grid container spacing={2}>
      <Grid
        item
        lg={5}
        md={12}
        xs={12}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Overview
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
          eletricity={electricityDisplay}
          fuel={fuelDisplay}
          water={waterDisplay}
          cost={totalEnergyDisplay}
          productCost={productCostDisplay}
          oeeDashboard={oeeDashboard}
          setOeeDashboard={setOeeDashboard}
        />
      </Grid>

      <Grid item lg={7} md={12} xs={12}>
        <OverviewBar
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
          data={oeeDashboard}
          oeeProcess={oeeProcess}
          oeeDashboard={oeeDashboard}
          setOeeDashboard={setOeeDashboard}
        />
      </Grid>

      <Grid item xs={12}>
        <StatusMachine
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
          statusMachine={oeeDashboard}
        />
      </Grid>
    </Grid>
  );
}